import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import { memberKeys } from "../members/queries";
import {
  executeRefund,
  findRefundForReconciliation,
  getRefundDetail,
  getRefunds,
} from "./api";
import type { RefundListQuery } from "./api";

export const refundKeys = {
  all: (sessionEpoch: number) => ["admin", sessionEpoch, "refunds"] as const,
  lists: (sessionEpoch: number) => [...refundKeys.all(sessionEpoch), "list"] as const,
  list: (sessionEpoch: number, query: RefundListQuery) => (
    [...refundKeys.lists(sessionEpoch), query] as const
  ),
  details: (sessionEpoch: number) => [...refundKeys.all(sessionEpoch), "detail"] as const,
  detail: (sessionEpoch: number, refundId: number) => (
    [...refundKeys.details(sessionEpoch), refundId] as const
  ),
};

export function useRefundList(query: RefundListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: refundKeys.list(session.sessionEpoch, query),
    queryFn: ({ signal }) => getRefunds(apiClient, query, signal),
    refetchOnMount: "always",
    staleTime: 0,
  });
}

export function useRefundDetail(refundId?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({
    queryKey: refundKeys.detail(session.sessionEpoch, refundId ?? 0),
    queryFn: ({ signal }) => getRefundDetail(apiClient, refundId!, signal),
    enabled: refundId !== undefined,
    staleTime: 0,
  });
}

export function useExecuteRefund() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (refundId: number) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await executeRefund(apiClient, refundId, request.controller.signal);
      } finally {
        request.release();
      }
    },
    onSuccess: async (_result, refundId) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: refundKeys.lists(session.sessionEpoch), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: refundKeys.detail(session.sessionEpoch, refundId), refetchType: "none" }),
        queryClient.invalidateQueries({ queryKey: memberKeys.all(session.sessionEpoch), refetchType: "none" }),
      ]);
    },
  });
}

export function useReconcileRefund() {
  const { apiClient, sessionLifecycle } = useAdminApplication();
  return useMutation({
    mutationFn: async ({
      refundId,
      query,
    }: {
      readonly refundId: number;
      readonly query: Omit<RefundListQuery, "status" | "page" | "size">;
    }) => {
      const request = sessionLifecycle.createRequestController();
      try {
        return await findRefundForReconciliation(
          apiClient,
          refundId,
          query,
          request.controller.signal,
        );
      } finally {
        request.release();
      }
    },
  });
}
